import { useState } from 'react';
import CampoTexto from '@/components/base/CampoTexto';
import CampoDinheiro from '@/components/base/CampoDinheiro';
import CampoArea from '@/components/base/CampoArea';
import OpcaoSelecao from '@/components/base/OpcaoSelecao';
import { hojeISO } from '../utils';
import type { MetodoPagamento } from '../types';

export interface PagamentoPayload {
  valor_centavos: number;
  pago_em: string;
  metodo: MetodoPagamento;
  observacao: string | null;
}

const METODOS: { valor: MetodoPagamento; label: string }[] = [
  { valor: 'pix', label: 'Pix' },
  { valor: 'dinheiro', label: 'Dinheiro' },
  { valor: 'transferencia', label: 'Transferência' },
  { valor: 'cartao', label: 'Cartão' },
  { valor: 'outro', label: 'Outro' },
];

interface Props {
  aoSalvar: (payload: PagamentoPayload) => void;
  aoCancelar: () => void;
  salvando: boolean;
  /** Valor já preenchido no campo (centavos), ex.: o que falta pagar. */
  valorInicialCentavos?: number;
  /** Texto de ajuda opcional do campo de valor. */
  ajudaValor?: string;
  /** Linha neutra no topo (ex.: "Suplente: ainda sem vaga."). Não bloqueia. */
  avisoTopo?: string | null;
  /** Erro vindo do servidor (ex.: a gravação falhou). Aparece acima do botão. */
  erroExterno?: string | null;
  /** Chamado quando o usuário edita um campo, para limpar o erro do servidor. */
  aoLimparErro?: () => void;
}

export default function FormRegistrarPagamento({
  aoSalvar,
  aoCancelar,
  salvando,
  valorInicialCentavos = 0,
  ajudaValor,
  avisoTopo,
  erroExterno,
  aoLimparErro,
}: Props) {
  const [valorCentavos, setValorCentavos] = useState(valorInicialCentavos);
  const [data, setData] = useState(hojeISO());
  const [metodo, setMetodo] = useState<MetodoPagamento | null>(null);
  const [observacao, setObservacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const erroMostrado = erro || erroExterno || null;

  function limpar() {
    setErro(null);
    aoLimparErro?.();
  }

  function enviar() {
    if (valorCentavos <= 0) {
      setErro('Informe o valor recebido.');
      return;
    }
    if (!data) {
      setErro('Informe a data do pagamento.');
      return;
    }
    if (!metodo) {
      setErro('Escolha como foi pago.');
      return;
    }
    setErro(null);
    const pago_em = new Date(`${data}T12:00:00`).toISOString();
    aoSalvar({
      valor_centavos: valorCentavos,
      pago_em,
      metodo,
      observacao: observacao.trim() || null,
    });
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center bg-black/30 sm:items-center sm:p-6">
      <div className="animate-modal-pop max-h-[90vh] w-full overflow-y-auto rounded-t-lg bg-white sm:max-w-md sm:rounded-lg">
        <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-4">
          <h3 className="text-lg font-medium text-foreground-950">Registrar pagamento</h3>
          <button
            type="button"
            onClick={aoCancelar}
            className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-secondary-500 hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="space-y-5 px-5 py-5">
          {avisoTopo && (
            <p className="rounded-md bg-secondary-100 px-3 py-2 text-sm font-medium text-secondary-900">
              {avisoTopo}
            </p>
          )}

          <CampoDinheiro
            id="pag-valor"
            name="pagamento_valor"
            label="Valor recebido"
            help={ajudaValor ?? 'Quanto a pessoa pagou agora. Pode ser uma parte do total.'}
            centavos={valorCentavos}
            onChange={(c) => {
              setValorCentavos(c);
              limpar();
            }}
            required
          />

          <CampoTexto
            id="pag-data"
            name="pagamento_data"
            label="Data do pagamento"
            help="Quando você recebeu. Já vem preenchido com hoje."
            type="date"
            value={data}
            onChange={(v) => {
              setData(v);
              limpar();
            }}
            required
          />

          <div>
            <span className="block text-base font-medium text-foreground-950">
              Como foi pago <span className="text-danger-600">*</span>
            </span>
            <p className="mt-1 text-sm text-secondary-500">
              Como o dinheiro chegou até a organização.
            </p>
            <div className="mt-1 space-y-1">
              {METODOS.map((m) => (
                <OpcaoSelecao
                  key={m.valor}
                  tipo="radio"
                  checked={metodo === m.valor}
                  onChange={() => {
                    setMetodo(m.valor);
                    limpar();
                  }}
                >
                  {m.label}
                </OpcaoSelecao>
              ))}
            </div>
          </div>

          <CampoArea
            id="pag-obs"
            name="pagamento_observacao"
            label="Observação"
            help="Opcional. Ex.: pago pela irmã dele, comprovante no grupo."
            value={observacao}
            onChange={(v) => {
              setObservacao(v);
              limpar();
            }}
            maxLength={500}
            simples
          />
        </div>

        {erroMostrado && (
          <p className="px-5 pb-3 text-sm font-medium text-danger-600" role="alert">
            {erroMostrado}
          </p>
        )}

        <div className="flex gap-3 border-t border-secondary-100 px-5 py-4">
          <button
            type="button"
            onClick={aoCancelar}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md border border-secondary-300 bg-white px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={enviar}
            disabled={salvando}
            className="flex-1 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600 disabled:opacity-60"
          >
            {salvando ? 'Salvando…' : 'Registrar'}
          </button>
        </div>
      </div>
    </div>
  );
}